import type { HeadersList } from '@carboncopyco/types';

export type HTTPMethod =
  | 'GET'
  | 'POST'
  | 'PUT'
  | 'PATCH'
  | 'DELETE'
  | 'HEAD'
  | 'OPTIONS';

export type HTTPStatusCategory =
  | 'informational'
  | 'successful'
  | 'redirection'
  | 'clientError'
  | 'serverError';

export enum ApplicationMIMEType {
  ApplicationJSON = 'application/json',
  ApplicationXML = 'application/xml',
  ApplicationJavascript = 'application/javascript',
  ApplicationEcmaScript = 'application/ecmascript',
  ApplicationXHTML = 'application/xhtml+xml',
}

export enum TextMIMEType {
  TextCSS = 'text/css',
  TextJavascript = 'text/javascript',
  TextEcmaScript = 'text/ecmascript',
  TextHTML = 'text/html',
  TextMarkdown = 'text/markdown',
  TextPlain = 'text/plain',
  TextXML = 'text/xml',
  TextYAML = 'text/yaml',
}

export enum ImageMIMEType {
  ImageAvif = 'image/avif',
  ImageBMP = 'image/bmp',
  ImageGIF = 'image/gif',
  ImageJPEG = 'image/jpeg',
  ImagePNG = 'image/png',
  ImageSVG = 'image/svg+xml',
  ImageTIFF = 'image/tiff',
  ImageWebP = 'image/webp',
}

export type HTTPContentTypeCategory =
  | 'text'
  | 'json'
  | 'image'
  | 'html'
  | 'css'
  | 'javascript';

export const contentTypeCategoryToMIMETypes: Record<
  HTTPContentTypeCategory,
  string[]
> = {
  text: [
    TextMIMEType.TextPlain,
    TextMIMEType.TextMarkdown,
    TextMIMEType.TextXML,
    TextMIMEType.TextYAML,
    ApplicationMIMEType.ApplicationXML,
    ApplicationMIMEType.ApplicationXHTML,
  ],
  json: [ApplicationMIMEType.ApplicationJSON],
  image: [...Object.values(ImageMIMEType)],
  html: [TextMIMEType.TextHTML, ApplicationMIMEType.ApplicationXHTML],
  css: [TextMIMEType.TextCSS],
  javascript: [
    TextMIMEType.TextJavascript,
    TextMIMEType.TextEcmaScript,
    ApplicationMIMEType.ApplicationJavascript,
    ApplicationMIMEType.ApplicationEcmaScript,
  ],
};

const statusCategoryToRange: Record<HTTPStatusCategory, [number, number]> = {
  informational: [100, 199],
  successful: [200, 299],
  redirection: [300, 399],
  clientError: [400, 499],
  serverError: [500, 599],
};

export const getStatusCategory = (httpStatus: number | string) => {
  for (const category of Object.keys(
    statusCategoryToRange
  ) as HTTPStatusCategory[]) {
    if (isStatusInCategory(+httpStatus, category)) {
      return category;
    }
  }
};

export const isStatusInCategory = (
  httpStatus: number,
  httpStatusCategory: HTTPStatusCategory
) => {
  const range = statusCategoryToRange[httpStatusCategory];
  return httpStatus >= range[0] && httpStatus <= range[1];
};

export const getContentType = (headers: HeadersList): string => {
  // By the HTTP spec there should be only one content-type header on the response
  const contentTypeHeader = headers.find(
    ({ name }) => name.toLowerCase() === 'content-type'
  );
  return contentTypeHeader?.value?.split(';')?.[0] ?? '';
};

export const getContentTypeCategory = (
  contentType: string
): HTTPContentTypeCategory | undefined => {
  return Object.entries(contentTypeCategoryToMIMETypes).find(([, mimeTypes]) =>
    mimeTypes.includes(contentType)
  )?.[0] as HTTPContentTypeCategory;
};

export function getPathFromUrl(url: string) {
  return url.split('?')[0];
}
